package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.foundation.Canvas
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.MerchantLocationsListViewModel
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p3
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p9
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p44
import com.example.mystybag.core.ui.theme.LocalTheme.p65
import com.example.mystybag.core.ui.theme.LocalTheme.p80
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.appCore.views.components.RatingDetailsDialog
import com.example.mystybag.sharedmodels.project.responseModels.merchant.LocationRatingResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.MerchantLocationResponseModel
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.min
import kotlin.math.sin

// Figma: Frame 26080650 itemSpacing 32; Frame 26080568 card 380×295, cornerRadius 28, padding 20, itemSpacing 16
// Rating Frame 26080583 (65dp): 4.6 + stars 32×30; Frame 26080651 itemSpacing 12; Button 340×48 cornerRadius 100

/** Detail row: label left; value Figma Title Small SemiBold 14, right-aligned. */
@Composable
private fun LocationDetailRow(
    label: String,
    value: String,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Texts.BodyMedium(
            text = label,
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier.weight(1f)
        )
        Texts.TitleSmall(
            text = value,
            color = AppTheme.current.colors.onSurface,
            maxLines = 1,
            textAlign = TextAlign.End,
            modifier = Modifier.widthIn(max = 200.dp)
        )
    }
}

/** Card per Figma Frame 26080568: 380×310, cornerRadius 28, padding 20, itemSpacing 16. */
@Composable
fun LocationCard(
    location: MerchantLocationResponseModel,
    ratingDetails: LocationRatingResponseModel?,
    /** Staff must not edit locations — hide the pencil when false. */
    showEditButton: Boolean = true,
    onCardClick: () -> Unit,
    onRatingClick: () -> Unit
) {
    val colors = AppTheme.current.colors
    val cardShape = RoundedCornerShape(p28)
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(p1, colors.outline, cardShape),
        shape = cardShape,
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        colors = CardDefaults.cardColors(containerColor = colors.surface)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p20),
            verticalArrangement = Arrangement.spacedBy(p16)
        ) {
            // Frame 26080569: 340×40, itemSpacing 12; title 288×24, menu 40×40
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p40),
                horizontalArrangement = Arrangement.spacedBy(p12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Texts.TitleLarge(
                    text = location.title ?: sharedString(SR.strings.location),
                    color = colors.onSurface,
                    modifier = Modifier
                        .weight(1f)
                        .height(p24)
                )
                if (showEditButton) {
                    // Edit (pencil) — Figma: primary circle + on-primary pencil (matches Bags list)
                    Box(
                        modifier = Modifier
                            .size(p40)
                            .clip(CircleShape)
                            .background(colors.primary)
                            .clickable { onCardClick() },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.staff_edit_icon),
                            contentDescription = stringResource(SR.strings.edit_short),
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.size(p20),
                            colorFilter = ColorFilter.tint(colors.onPrimary)
                        )
                    }
                }
            }

            // Rectangle 36: 340×1
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth(),
                thickness = p1,
                color = colors.outline.copy(alpha = 0.4f)
            )

            // Frame 26080565: 340×182, itemSpacing 16 (Figma)
            Column(verticalArrangement = Arrangement.spacedBy(p16)) {
                val hasReviews = (ratingDetails?.reviewCount ?: 0) > 0
                if (hasReviews) {
                    // Frame 26080583: rating block — tappable to open "Общ рейтинг" dialog
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .rippleClick(padding = p0, roundPadding = p0) { onRatingClick() },
                        verticalArrangement = Arrangement.spacedBy(5.dp)
                    ) {
                        val ratingValue = ratingDetails?.averageRating?.toDouble()?.coerceIn(0.0, 5.0) ?: 0.0
                        // Frame 26080582: rating number + stars — aligned with "Локация:" (same start as detail rows)
                        Row(
                            modifier = Modifier.fillMaxWidth().height(44.dp),
                            horizontalArrangement = Arrangement.spacedBy(p10),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Texts.DisplaySmall(
                                text = com.example.mystybag.core.expected.format("%.1f", ratingValue),
                                color = colors.onSurface,
                                modifier = Modifier.height(44.dp)
                            )
                            Row(
                                modifier = Modifier
                                    .height(30.dp),
                                horizontalArrangement = Arrangement.spacedBy(p9),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                val starWidth = 32.dp
                                val starHeight = 30.dp
                                val filledStarColor = Color(0xFFEFC200)
                                val unfilledStarColor = Color(0xFFDDDFDE)
                                repeat(5) { index ->
                                    val fillFraction = (ratingValue - index).coerceIn(0.0, 1.0)
                                    FractionalStar(
                                        modifier = Modifier.size(starWidth, starHeight),
                                        fillFraction = fillFraction.toFloat(),
                                        filledColor = filledStarColor,
                                        unfilledColor = unfilledStarColor
                                    )
                                }
                            }
                        }
                        // "X отзива" — green, underlined
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(min = 20.dp)
                                .padding(vertical = 2.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Texts.BodySmall(
                                text = sharedString(SR.strings.reviews_count, ratingDetails!!.reviewCount),
                                color = colors.primary,
                                textDecoration = TextDecoration.Underline
                            )
                        }
                    }

                    // Rectangle 37: 340×1
                    HorizontalDivider(
                        modifier = Modifier.fillMaxWidth(),
                        thickness = p1,
                        color = colors.outline.copy(alpha = 0.4f)
                    )
                }

                // Frame 26080651: 340×84, itemSpacing 12 — Location, Employees, Active bags
                Column(verticalArrangement = Arrangement.spacedBy(p12)) {
                    val locationText = listOfNotNull(location.address, location.cityName)
                        .filter { it.isNotEmpty() }
                        .joinToString(", ")
                        .ifEmpty { "—" }
                    LocationDetailRow(
                        label = sharedString(SR.strings.location_label),
                        value = locationText
                    )
                    LocationDetailRow(
                        label = sharedString(SR.strings.employees_label),
                        value = if (location.employeesCount > 0) location.employeesCount.toString() else "—"
                    )
                    LocationDetailRow(
                        label = sharedString(SR.strings.active_bags_label),
                        value = location.activeBagsCount.toString()
                    )
                }
            }
        }
    }
}

@Composable
private fun FractionalStar(
    modifier: Modifier,
    fillFraction: Float,
    filledColor: Color,
    unfilledColor: Color
) {
    Canvas(modifier = modifier) {
        val clampedFraction = fillFraction.coerceIn(0f, 1f)
        val clipFraction = areaAdjustedClipFraction(clampedFraction)
        val starPath = createStarPath(size.width, size.height)
        drawPath(path = starPath, color = unfilledColor)
        clipRect(right = size.width * clipFraction) {
            drawPath(path = starPath, color = filledColor)
        }
    }
}

private fun areaAdjustedClipFraction(fillFraction: Float): Float {
    val targetAreaFraction = fillFraction.coerceIn(0f, 1f)
    val areaStops = floatArrayOf(
        0f, 0.047f, 0.093f, 0.166f, 0.242f, 0.316f, 0.406f, 0.5f,
        0.6f, 0.684f, 0.762f, 0.834f, 0.91f, 0.953f, 0.976f, 0.991f, 1f
    )
    val clipStops = floatArrayOf(
        0f, 0.2f, 0.25f, 0.3f, 0.35f, 0.4f, 0.45f, 0.5f,
        0.55f, 0.6f, 0.65f, 0.7f, 0.75f, 0.8f, 0.85f, 0.9f, 1f
    )
    for (i in 0 until areaStops.lastIndex) {
        val a0 = areaStops[i]
        val a1 = areaStops[i + 1]
        if (targetAreaFraction <= a1) {
            val t = ((targetAreaFraction - a0) / (a1 - a0)).coerceIn(0f, 1f)
            return clipStops[i] + (clipStops[i + 1] - clipStops[i]) * t
        }
    }
    return 1f
}

private fun createStarPath(width: Float, height: Float): Path {
    val path = Path()
    val cx = width / 2f
    val cy = height / 2f
    val outerRadius = min(width, height) / 2f
    val innerRadius = outerRadius * 0.5f
    val startAngle = -PI / 2.0

    repeat(10) { pointIndex ->
        val angle = startAngle + (PI / 5.0) * pointIndex
        val radius = if (pointIndex % 2 == 0) outerRadius else innerRadius
        val x = cx + cos(angle).toFloat() * radius
        val y = cy + sin(angle).toFloat() * radius
        if (pointIndex == 0) {
            path.moveTo(x, y)
        } else {
            path.lineTo(x, y)
        }
    }
    path.close()
    return path
}

@Composable
fun MerchantLocationsListView(viewModel: MerchantLocationsListViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    var locationIdForRatingDialog by remember { mutableStateOf<Long?>(null) }
    val fixedHeaderHeight = p44
    val useNativeStyleWebLayout = rememberUseNativeStyleWebLayout()

    val colors = AppTheme.current.colors
    UIView(
        modifier = Modifier
            .fillMaxSize()
            .background(colors.background),
        viewModel = viewModel,
        showTopBar = false,
        applyBottomInsetSpacer = false,
        removeVerticalPadding = true
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(start = p16, end = p16, top = p16)
                    // Keep list content within app design width on large screens.
                    .widthIn(max = 412.dp)
                    .align(Alignment.TopCenter)
            ) {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .scrollEdgeFade(showTop = false),
                    verticalArrangement = Arrangement.spacedBy(p16)
                ) {
                    if (useNativeStyleWebLayout) {
                        item {
                            Texts.DisplaySmall(
                                text = stringResource(SR.strings.objects),
                                modifier = Modifier.fillMaxWidth(),
                                color = colors.onSurface
                            )
                            Spacer(modifier = Modifier.height(p12))
                        }
                    }

                    items(uiState.locations, key = { it.id }) { location ->
                        LocationCard(
                            location = location,
                            ratingDetails = uiState.ratingsByLocation[location.id],
                            showEditButton = !uiState.isStaff,
                            onCardClick = { viewModel.onLocationClick(location) },
                            onRatingClick = { locationIdForRatingDialog = location.id }
                        )
                    }
                }

                if (!uiState.isStaff && PlatformHelper.isWeb) {
                    Spacer(modifier = Modifier.height(p16))
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth(0.5f)
                            .align(Alignment.CenterHorizontally)
                            .height(p56)
                            .padding(bottom = 32.dp),
                        caption = stringResource(SR.strings.add_location),
                        capitalization = CaptionCapitalization.None,
                        contentPadding = PaddingValues(horizontal = p16, vertical = 0.dp),
                        callback = { viewModel.onAddLocationClick() },
                    )
                }
            }

            if (locationIdForRatingDialog != null) {
                val loc = uiState.locations.find { it.id == locationIdForRatingDialog }
                val details = locationIdForRatingDialog?.let { uiState.ratingsByLocation[it] }
                RatingDetailsDialog(
                    averageRating = details?.averageRating ?: 0.0,
                    reviewCount = details?.reviewCount ?: 0,
                    businessCategoryId = loc?.businessCategoryId,
                    ratingCategory1 = details?.ratingCategory1 ?: 0.0,
                    ratingCategory2 = details?.ratingCategory2 ?: 0.0,
                    ratingCategory3 = details?.ratingCategory3 ?: 0.0,
                    ratingCategory4 = details?.ratingCategory4 ?: 0.0,
                    onDismiss = { locationIdForRatingDialog = null }
                )
            }

            // FAB "Добави локация" — скрит за служители (локации могат да създават само собствениците)
            if (!uiState.isStaff && !PlatformHelper.isWeb) {
                FloatingActionButton(
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .padding(p20)
                        .size(p80),
                    containerColor = colors.inversePrimary,
                    contentColor = colors.onPrimary,
                    shape = CircleShape,
                    onClick = { viewModel.onAddLocationClick() }
                ) {
                    Box(
                        modifier = Modifier.size(28.dp, 28.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(modifier = Modifier.size(2.dp, 28.dp).background(colors.onPrimary))
                        Box(modifier = Modifier.size(28.dp, 2.dp).background(colors.onPrimary))
                    }
                }
            }
        }
    }
}
